import React, { useState, useEffect } from 'react';
import { getCampaigns } from '../../services/campaignService';
import { getCustomers } from '../../services/customerService';
import { getScheduledEmails, scheduleEmail, deleteScheduledEmail } from '../../services/scheduleService';
import { getFunctions, httpsCallable } from 'firebase/functions';

const SendSchedule = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sendOption, setSendOption] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [processingEmail, setProcessingEmail] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Try to fetch campaigns and customers, handling each separately
        let campaignsData = [];
        let customersData = [];
        let schedulesData = [];
        
        try {
          campaignsData = await getCampaigns();
        } catch (campaignErr) {
          console.error('Error fetching campaigns:', campaignErr);
          // Continue with empty campaigns rather than failing completely
        }
        
        try {
          customersData = await getCustomers();
        } catch (customerErr) {
          console.error('Error fetching customers:', customerErr);
          // Continue with empty customers rather than failing completely
        }
        
        try {
          schedulesData = await getScheduledEmails();
        } catch (scheduleErr) {
          console.error('Error fetching scheduled emails:', scheduleErr);
          // Continue with empty schedules rather than failing completely
        }
        
        setCampaigns(campaignsData);
        setCustomers(customersData);
        setScheduledEmails(schedulesData);
        
        // Only show error if we couldn't get any data at all
        if (campaignsData.length === 0 && customersData.length === 0) {
          setError('No campaigns or customers found. Please create some first.');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Set tomorrow as the default scheduled date
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setScheduledDate(tomorrow.toISOString().split('T')[0]);
    setScheduledTime('09:00');
  }, []);

  // Handle select all checkbox
  useEffect(() => {
    if (selectAll) {
      setSelectedRecipients(customers.map(customer => customer.id));
    } else if (selectedRecipients.length === customers.length) {
      setSelectedRecipients([]);
    }
  }, [selectAll, customers, selectedRecipients.length]);

  const handleRecipientSelect = (customerId) => {
    setSelectedRecipients(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  const handleSelectAllChange = () => {
    setSelectAll(!selectAll);
  };

  const validateForm = () => {
    if (!selectedCampaign) {
      setError('Please select a campaign');
      return false;
    }
    
    if (selectedRecipients.length === 0) {
      setError('Please select at least one recipient');
      return false;
    }
    
    if (sendOption === 'schedule') {
      if (!scheduledDate || !scheduledTime) {
        setError('Please select a date and time for scheduling');
        return false;
      }
      
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        setError('Scheduled time must be in the future');
        return false;
      }
    }
    
    setError(null);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setShowConfirmation(true);
  };

  const confirmSend = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      const campaign = campaigns.find(c => c.id === selectedCampaign);
      const selectedCustomers = customers.filter(c => selectedRecipients.includes(c.id));

      let scheduledFor = null;
      if (sendOption === 'schedule') {
        scheduledFor = new Date(`${scheduledDate}T${scheduledTime}`);
      } else {
        // For immediate sending, set schedule for 30 seconds from now
        scheduledFor = new Date(Date.now() + 30000);
      }
      
      console.log("Scheduling email with data:", {
        campaignId: selectedCampaign,
        campaignTitle: campaign.title,
        recipientsCount: selectedCustomers.length,
        scheduledFor,
        immediate: sendOption === 'now'
      });
      
      const docRef = await scheduleEmail({
        campaignId: selectedCampaign,
        campaignTitle: campaign.title,
        recipients: selectedCustomers.map(c => ({ 
          id: c.id,
          name: c.name, 
          email: c.email 
        })),
        scheduledFor,
        immediate: sendOption === 'now'
      });
      
      console.log("Email scheduled with document ID:", docRef?.id);
      
      setSuccess(sendOption === 'now' 
        ? 'Email has been queued and will be sent shortly!' 
        : 'Email has been scheduled successfully!');
      
      // Reset form
      setSelectedCampaign('');
      setSelectedRecipients([]);
      setSelectAll(false);
      setSendOption('now');
      
      // Refresh scheduled emails list
      const updatedSchedules = await getScheduledEmails();
      setScheduledEmails(updatedSchedules);
    } catch (err) {
      console.error('Error scheduling email:', err);
      setError(`Failed to schedule email: ${err.message}. Check console for details.`);
    } finally {
      setShowConfirmation(false);
      setSubmitting(false);
    }
  };

  const handleDeleteSchedule = async (id) => {
    try {
      setLoading(true);
      await deleteScheduledEmail(id);
      setScheduledEmails(scheduledEmails.filter(email => email.id !== id));
      setSuccess('Scheduled email has been removed from the list.');
    } catch (err) {
      console.error('Error deleting scheduled email:', err);
      setError('Failed to remove email from the list.');
    } finally {
      setLoading(false);
    }
  };

  const manualSendNow = async (scheduleId) => {
    try {
      setProcessingEmail(scheduleId);
      
      const functions = getFunctions(undefined, 'us-central1');
      // Remove 'sender-' prefix here too
      const manualSendEmailFn = httpsCallable(functions, 'manualSendEmail');
      
      const result = await manualSendEmailFn({ scheduleId });
      console.log("Manual send result:", result.data);
      
      if (result.data.success) {
        setSuccess(`Email sent successfully! ${result.data.sent} delivered, ${result.data.failed} failed.`);
        
        // Refresh scheduled emails list
        const updatedSchedules = await getScheduledEmails();
        setScheduledEmails(updatedSchedules);
      } else {
        setError('Failed to send email. Please try again.');
      }
    } catch (err) {
      console.error('Error manually sending email:', err);
      setError(`Failed to send email: ${err.message}`);
    } finally {
      setProcessingEmail(null);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    
    // Use native JS date formatting
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric',
      minute: '2-digit', 
      hour12: true 
    };
    return new Date(date).toLocaleString('en-US', options);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Skicka & Schemalägg E-post</h3>
        <p className="mt-1 text-sm text-gray-500">Planera och schemalägg dina e-postkampanjer.</p>
      </div>
      
      {/* Show error with a more helpful message */}
      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
          {campaigns.length === 0 && (
            <p className="mt-1">
              Go to the "Email Campaigns" tab to create a campaign first.
            </p>
          )}
        </div>
      )}
      
      {/* Show success message */}
      {success && (
        <div className="mx-6 mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}
      
      {/* Display a message if no campaigns exist */}
      {!loading && campaigns.length === 0 ? (
        <div className="p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Email Campaigns Available</h3>
          <p className="mt-1 text-sm text-gray-500">
            You need to create email campaigns before you can schedule them.
          </p>
          <div className="mt-6">
            <a
              href="#/campaigns"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={(e) => {
                e.preventDefault();
                // Navigate to the campaigns tab
                window.location.hash = '#campaigns';
              }}
            >
              Create Email Campaign
            </a>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label htmlFor="campaign" className="block text-sm font-medium text-gray-700 mb-1">
              Välj kampanj*
            </label>
            <select
              id="campaign"
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              required
            >
              <option value="">-- Välj en kampanj --</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>{campaign.title}</option>
              ))}
            </select>
          </div>
          
          <div className="mb-6">
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">Välj mottagare*</label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="selectAll"
                  checked={selectAll}
                  onChange={handleSelectAllChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="selectAll" className="ml-2 text-sm text-gray-700">
                  Välj alla
                </label>
              </div>
            </div>
            
            <div className="mt-2 max-h-60 overflow-y-auto border border-gray-300 rounded-md p-2">
              {customers.length === 0 ? (
                <p className="text-gray-500 text-sm p-2">No customers found.</p>
              ) : (
                customers.map(customer => (
                  <div key={customer.id} className="flex items-center py-2 px-1 border-b border-gray-200 last:border-0">
                    <input
                      type="checkbox"
                      id={`customer-${customer.id}`}
                      value={customer.id}
                      checked={selectedRecipients.includes(customer.id)}
                      onChange={() => handleRecipientSelect(customer.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`customer-${customer.id}`} className="ml-3 block text-sm text-gray-700">
                      <span className="font-medium">{customer.name}</span> <span className="text-gray-500">({customer.email})</span>
                    </label>
                  </div>
                ))
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {selectedRecipients.length} av {customers.length} mottagare valda
            </p>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              När ska det skickas
            </label>
            <div className="mt-2 space-y-4 sm:space-y-0 sm:flex sm:items-center sm:space-x-6">
              <div className="flex items-center">
                <input
                  id="send-now"
                  name="send-option"
                  type="radio"
                  value="now"
                  checked={sendOption === 'now'}
                  onChange={() => setSendOption('now')}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <label htmlFor="send-now" className="ml-3 block text-sm text-gray-700">
                  Skicka direkt
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="schedule"
                  name="send-option"
                  type="radio"
                  value="schedule"
                  checked={sendOption === 'schedule'}
                  onChange={() => setSendOption('schedule')}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <label htmlFor="schedule" className="ml-3 block text-sm text-gray-700">
                  Schemalägg för senare
                </label>
              </div>
            </div>
          </div>
          
          {sendOption === 'schedule' && (
            <div className="mb-6 p-4 bg-gray-50 rounded-md">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="scheduled-date" className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    id="scheduled-date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    required={sendOption === 'schedule'}
                  />
                </div>
                <div>
                  <label htmlFor="scheduled-time" className="block text-sm font-medium text-gray-700 mb-1">
                    Time
                  </label>
                  <input
                    type="time"
                    id="scheduled-time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    required={sendOption === 'schedule'}
                  />
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300"
            >
              {submitting ? 'Bearbetar...' : sendOption === 'now' ? 'Skicka nu' : 'Schemalägg e-post'}
            </button>
          </div>
        </form>
      )}

      {/* Scheduled Emails List - Only show pending emails */}
      {scheduledEmails.filter(email => email.status === 'pending').length > 0 && (
        <div className="p-6 border-t border-gray-200">
          <h4 className="text-base font-medium text-gray-900 mb-4">Schemalagda E-postmeddelanden</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kampanj
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mottagare
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schemalagd För
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Åtgärder
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scheduledEmails.filter(email => email.status === 'pending').map((email) => (
                  <tr key={email.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {email.campaignTitle}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {email.recipients.length} mottagare
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(email.scheduledFor)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Väntande
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(email.scheduledFor) > new Date() ? (
                        <button
                          onClick={() => handleDeleteSchedule(email.id)}
                          className="text-red-600 hover:text-red-900 mr-2"
                        >
                          Avbryt
                        </button>
                      ) : (
                        <button
                          onClick={() => manualSendNow(email.id)}
                          disabled={processingEmail === email.id}
                          className="text-blue-600 hover:text-blue-900 mr-2 disabled:text-gray-400"
                        >
                          {processingEmail === email.id ? 'Skickar...' : 'Skicka Nu'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen">
            <div className="fixed inset-0 bg-black opacity-30" onClick={() => setShowConfirmation(false)}></div>
            
            <div className="relative bg-white rounded-lg max-w-md w-full mx-auto p-6 shadow-xl">
              <h2 className="text-lg font-medium text-gray-900">
                Confirm {sendOption === 'now' ? 'Send' : 'Schedule'}
              </h2>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  You are about to {sendOption === 'now' ? 'send' : 'schedule'} the campaign "{campaigns.find(c => c.id === selectedCampaign)?.title}" 
                  to {selectedRecipients.length} recipient(s)
                  {sendOption === 'schedule' ? ` on ${scheduledDate} at ${scheduledTime}` : ''}.
                  Are you sure you want to continue?
                </p>
              </div>

              <div className="mt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => setShowConfirmation(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={confirmSend}
                >
                  {sendOption === 'now' ? 'Send Now' : 'Schedule Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendSchedule;
